import type { MapBrowserEvent } from 'ol';
import { Feature } from 'ol';
import type { Geometry } from 'ol/geom';
import { Polygon } from 'ol/geom';
import type { SelectEvent } from 'ol/interaction/Select';

import type { TacticalAnnotationDTO } from '@nora-monorepo/nora-api-client';
import { DrawToolType } from '@nora-monorepo/nora-api-client';
import { isDefined } from '@nora-monorepo/shared';

import { CreateFeatureBaseDrawTool } from '#nora-ui/map/drawtools/create-feature-base-draw-tool';
import useTacticalAnnotationToolStore, {
  TacticalAnnotationToolMode,
} from '#nora-ui/map/drawtools/stores/tactical-annotation-tool-store';
import { getMoveHandleStyle } from '#nora-ui/map/drawtools/styles/handle-style';
import { tacticalAnnotationStyleFunction } from '#nora-ui/map/drawtools/styles/tactical-annotation-style';
import { AREA_CORNERS, TacticalAnnotationCalculator } from '#nora-ui/map/drawtools/tactical-annotation/tactical-annotation-calculator';
import { TacticalAnnotationFeatureManager } from '#nora-ui/map/drawtools/tactical-annotation/tactical-annotation-feature-manager';
import { TacticalAnnotationInteractionManager } from '#nora-ui/map/drawtools/tactical-annotation/tactical-annotation-interaction-manager';
import { TacticalAnnotationPointManager } from '#nora-ui/map/drawtools/tactical-annotation/tactical-annotation-point-manager';
import type { DrawToolOptions } from '#nora-ui/map/drawtools/types';
import { extractDrawToolOptions } from '#nora-ui/map/drawtools/types';
import type { DrawToolManager } from '#nora-ui/map/map/DrawToolManager';

/**
 * Tactical Annotation Tool allows users to annotate buildings by:
 * 1. Drawing a 4-sided polygon by clicking 4 points on the map
 * 2. Selecting which side represents the building entry
 * 3. Automatically applying color-coded visualization to all sides
 *
 * The color pattern (relative to the selected entry side):
 * - White: Selected side (building entry)
 * - Red: To the right of building entry
 * - Black: Opposite side from entry
 * - Green: To the left of building entry
 */
export class TacticalAnnotationTool extends CreateFeatureBaseDrawTool {
  drawToolType: DrawToolType = DrawToolType.tactical_annotation;
  options: DrawToolOptions = { drawToolType: this.drawToolType };

  private readonly drawingPhaseManager: TacticalAnnotationPointManager;
  private readonly featureManager: TacticalAnnotationFeatureManager;
  private readonly interactionManager: TacticalAnnotationInteractionManager;

  private polygonFeature: Feature<Polygon> | null = null;

  tacticalAnnotationToolStore = useTacticalAnnotationToolStore(this.drawToolManager.map.getId());

  private readonly boundOnMapClick: (event: MapBrowserEvent) => void;
  private readonly boundOnPointerMove: (event: MapBrowserEvent) => void;
  private readonly boundOnGeometryChange: () => void;

  /**
   * Constructs a new TacticalAnnotationTool instance.
   */
  constructor(drawToolManager: DrawToolManager) {
    super(drawToolManager);
    this.drawToolName = 'Tactical Annotation';

    this.featureManager = new TacticalAnnotationFeatureManager(
      this.editingVectorSource,
      this.featureId ?? '',
    );
    this.drawingPhaseManager = new TacticalAnnotationPointManager(this.editingVectorSource);
    this.interactionManager = new TacticalAnnotationInteractionManager(
      this.editingVectorLayer,
    );

    this.editingVectorLayer.setStyle((feature) => {
      const isHandle = feature.get('isCornerHandle') === true;
      const isPointIndicator = feature.get('isPointIndicator') === true;

      const baseStyle = (isHandle || isPointIndicator)
        ? getMoveHandleStyle(feature)
        : tacticalAnnotationStyleFunction(feature);

      const zIndex = (feature.get('zIndex') as number | undefined) ?? (isHandle ? 100 : isPointIndicator ? 50 : 1);

      if (Array.isArray(baseStyle)) {
        for (const style of baseStyle) {
          style.setZIndex(zIndex);
        }
      }
      else {
        baseStyle.setZIndex(zIndex);
      }

      return baseStyle;
    });

    this.boundOnMapClick = this.onMapClick.bind(this);
    this.boundOnPointerMove = this.onPointerMove.bind(this);
    this.boundOnGeometryChange = this.onGeometryChange.bind(this);

    this.interactionManager.selectInteraction.on('select', this.onSelect.bind(this));
    this.interactionManager.modifyInteraction.on('modifystart', this.onModifyStart.bind(this));
    this.interactionManager.modifyInteraction.on('modifyend', this.onModifyEnd.bind(this));
  }

  /**
   * Activates the tactical annotation tool.
   */
  override activate(): void {
    super.activate();

    const olMap = this.drawToolManager.map.getOlMapOrThrow();

    this.interactionManager.activate(olMap);
    olMap.on('click', this.boundOnMapClick);
    olMap.on('pointermove', this.boundOnPointerMove);
  }

  /**
   * Deactivates the tactical annotation tool.
   */
  override deactivate(): void {
    super.deactivate();

    const olMap = this.drawToolManager.map.getOlMapOrThrow();

    this.interactionManager.deactivate(olMap);
    olMap.un('click', this.boundOnMapClick);
    olMap.un('pointermove', this.boundOnPointerMove);
    this.resetAnnotation();
  }

  /**
   * Resets the annotation tool to its initial state.
   */
  private resetAnnotation(
    newMode: TacticalAnnotationToolMode = TacticalAnnotationToolMode.CreateAreaPolygon,
  ): void {
    const polygonFeature = this.polygonFeature;
    if (polygonFeature) {
      this.editingVectorSource.removeFeature(polygonFeature);
    }

    this.tacticalAnnotationToolStore.reset();
    this.polygonFeature = null;
    this.feature = null;
    this.drawingPhaseManager.reset();
    this.featureManager.clear(this.interactionManager.modifyFeatures);
    this.interactionManager.modifyFeatures.clear();
    this.tacticalAnnotationToolStore.mode = newMode;
  }

  /**
   * Handles map click events during the drawing phase.
   */
  private onMapClick(event: MapBrowserEvent): void {
    if (this.tacticalAnnotationToolStore.mode !== TacticalAnnotationToolMode.CreateAreaPolygon) {
      return;
    }

    this.drawingPhaseManager.addPoint(event.coordinate);
    this.featureManager.addPointIndicator(
      event.coordinate,
      this.interactionManager.modifyFeatures,
    );

    if (!this.drawingPhaseManager.hasFinishedPointSelection()) {
      this.drawingPhaseManager.updatePreview();
      return;
    }

    this.createPolygon();
    this.featureManager.removePointIndicators(this.interactionManager.modifyFeatures);
  }

  /**
   * Handles pointer move events to show preview lines while drawing.
   */
  private onPointerMove(event: MapBrowserEvent): void {
    const clickedPoints = this.drawingPhaseManager.getClickedPoints();

    if (
      this.tacticalAnnotationToolStore.mode !== TacticalAnnotationToolMode.CreateAreaPolygon
      || clickedPoints.length === 0
      || this.drawingPhaseManager.hasFinishedPointSelection()
      || this.interactionManager.getIsDragging()
    ) {
      return;
    }

    this.drawingPhaseManager.updatePreview(event.coordinate);
  }

  /**
   * Creates the building polygon after 4 points have been clicked.
   */
  private createPolygon(): void {
    const clickedPoints = this.drawingPhaseManager.getClickedPoints();
    if (clickedPoints.length !== AREA_CORNERS) {
      return;
    }

    const normalizedPoints = TacticalAnnotationCalculator.normalizeDirection(clickedPoints);
    const polygonCoordinates = TacticalAnnotationCalculator.closePolygon(normalizedPoints);

    const polygonFeature = new Feature({
      geometry: new Polygon([polygonCoordinates]),
    });

    polygonFeature.setProperties({
      ...this.getDefaultProperties(),
      id: this.featureId,
      fillColor: 'transparent',
      preventSelection: true,
    });

    polygonFeature.setStyle(tacticalAnnotationStyleFunction);

    this.drawingPhaseManager.clearPreview();
    this.editingVectorSource.addFeature(polygonFeature);
    this.interactionManager.modifyFeatures.push(polygonFeature);

    this.featureManager.createSideFeatures(polygonFeature, this.getDefaultProperties());
    this.featureManager.createCornerHandles(polygonFeature);

    this.polygonFeature = polygonFeature;
    this.feature = polygonFeature;
    this.markDirty();
    this.tacticalAnnotationToolStore.mode = TacticalAnnotationToolMode.SelectEntrySide;
  }

  /**
   * Handles the start of a modification (dragging corners or point indicators).
   */
  private onModifyStart(): void {
    const olMap = this.drawToolManager.map.getOlMapOrThrow();

    this.interactionManager.setDragging(true, olMap);

    // Handle polygon modification (after drawing is complete)
    const geometry = this.polygonFeature?.getGeometry();
    if (geometry) {
      geometry.on('change', this.boundOnGeometryChange);
      return;
    }

    // Handle point indicator modification (during drawing phase)
    if (this.tacticalAnnotationToolStore.mode === TacticalAnnotationToolMode.CreateAreaPolygon) {
      const pointIndicators = this.featureManager.getPointIndicators();
      for (const indicator of pointIndicators) {
        const indicatorGeometry = indicator.getGeometry();
        if (indicatorGeometry) {
          indicatorGeometry.on('change', this.boundOnGeometryChange);
        }
      }
    }
  }

  /**
   * Handles real-time geometry changes during polygon modification.
   */
  private onGeometryChange(): void {
    // Handle point indicator changes during drawing phase
    if (this.tacticalAnnotationToolStore.mode === TacticalAnnotationToolMode.CreateAreaPolygon) {
      this.updateClickedPointsFromIndicators();
      this.drawingPhaseManager.updatePreview();
      return;
    }

    // Handle polygon changes after drawing is complete
    if (!this.polygonFeature) {
      return;
    }

    this.featureManager.updateSideFeatures(this.polygonFeature);
    this.featureManager.updateCornerHandles(this.polygonFeature);

    const lines = this.featureManager.getLines();
    if (lines.length === 0) {
      return;
    }

    this.featureManager.updateLinesFromFeatures();
    this.persistTacticalAnnotation();
  }

  /**
   * Updates the clicked points array from the current positions of point indicators.
   */
  private updateClickedPointsFromIndicators(): void {
    const pointIndicators = this.featureManager.getPointIndicators();
    const updatedPoints = pointIndicators.map(indicator => indicator.getGeometry()?.getCoordinates()).filter(isDefined);

    this.drawingPhaseManager.updateClickedPoints(updatedPoints);
  }

  /**
   * Handles the end of a modification.
   */
  private onModifyEnd(): void {
    const olMap = this.drawToolManager.map.getOlMapOrThrow();

    this.interactionManager.setDragging(false, olMap);

    // Clean up geometry change listeners from point indicators after dragging ends during point placement
    if (this.tacticalAnnotationToolStore.mode === TacticalAnnotationToolMode.CreateAreaPolygon) {
      const pointIndicators = this.featureManager.getPointIndicators();
      for (const indicator of pointIndicators) {
        const indicatorGeometry = indicator.getGeometry();
        if (indicatorGeometry) {
          indicatorGeometry.un('change', this.boundOnGeometryChange);
        }
      }
      return;
    }

    if (!this.polygonFeature) {
      return;
    }

    const geometry = this.polygonFeature.getGeometry();
    if (geometry) {
      geometry.un('change', this.boundOnGeometryChange);
    }

    if (!geometry) {
      return;
    }

    const coordinates = geometry.getCoordinates()[0];
    if (coordinates.length !== AREA_CORNERS + 1) {
      return;
    }

    this.featureManager.updateSideFeatures(this.polygonFeature);
    this.featureManager.updateCornerHandles(this.polygonFeature);

    const lines = this.featureManager.getLines();
    if (lines.length === 0) {
      return;
    }

    this.featureManager.updateLinesFromFeatures();
    this.persistTacticalAnnotation();
    this.markDirty();
  }

  /**
   * Handles side selection when a user clicks on a building side line.
   */
  private onSelect(event: SelectEvent): void {
    if (event.selected.length === 0) {
      return;
    }

    const selectedFeature = event.selected[0];
    const lineFeatures = this.featureManager.getLineFeatures();

    if (!lineFeatures.includes(selectedFeature)) {
      return;
    }

    this.tacticalAnnotationToolStore.mode = TacticalAnnotationToolMode.Confirmation;

    const selectedIndex = selectedFeature.get('sideIndex') as number | undefined;
    if (selectedIndex === undefined) {
      return;
    }

    this.featureManager.reorderLinesToMakeIndexZero(selectedIndex);
    this.featureManager.updateLinesFromFeatures();
    this.persistTacticalAnnotation();
    this.markDirty();
  }

  /**
   * Persists the current tactical annotation data to the polygon feature properties.
   */
  private persistTacticalAnnotation(): void {
    if (!this.polygonFeature) {
      return;
    }

    this.polygonFeature.setProperties({
      ...this.getDefaultProperties(),
      id: this.featureId,
      tacticalAnnotation: {
        lines: this.featureManager.getLines(),
      },
      preventSelection: true,
    });

    this.feature = this.polygonFeature;
  }

  override showStyles(): boolean {
    return false;
  }

  override onStylingUpdated(): void {
    // Do nothing since we are setting the fill colors manually for this feature.
  }

  override dismiss() {
    super.dismiss();
    this.resetAnnotation();
  }

  /**
   * Loads an existing tactical annotation feature for editing.
   */
  override loadFeature(feature: Feature<Geometry>): boolean {
    this.resetAnnotation(TacticalAnnotationToolMode.Confirmation);

    const featureProperties = extractDrawToolOptions(feature);
    this.drawToolManager.updateDrawStyleOptions(featureProperties);

    const tacticalAnnotation = feature.get('tacticalAnnotation') as TacticalAnnotationDTO | undefined;
    if (!tacticalAnnotation) {
      return false;
    }

    const lines = tacticalAnnotation.lines;
    if (lines.length !== AREA_CORNERS) {
      return false;
    }

    const featureGeometry = feature.getGeometry();
    if (!featureGeometry) {
      return false;
    }

    const geometry = featureGeometry.clone();
    if (!(geometry instanceof Polygon)) {
      return false;
    }

    let coordinates = geometry.getCoordinates()[0];
    if (coordinates.length !== AREA_CORNERS + 1) {
      return false;
    }

    // Normalize polygon line direction to keep colors the same regardless if the creation direction is clockwise or counter-clockwise.
    const cornersOnly = coordinates.slice(0, AREA_CORNERS);
    const normalizedCorners = TacticalAnnotationCalculator.normalizeDirection(cornersOnly);
    coordinates = TacticalAnnotationCalculator.closePolygon(normalizedCorners);
    geometry.setCoordinates([coordinates]);

    this.featureId = feature.get('id') as string | undefined;

    // Create polygon feature
    const polygonFeature = new Feature({ geometry });
    polygonFeature.setProperties({
      ...this.getDefaultProperties(),
      id: this.featureId,
      tacticalAnnotation,
      preventSelection: true,
    });
    polygonFeature.setStyle(tacticalAnnotationStyleFunction);

    this.editingVectorSource.addFeature(polygonFeature);
    this.interactionManager.modifyFeatures.push(polygonFeature);

    // Load line features
    this.featureManager.loadLinesFromData(
      lines,
      coordinates,
      this.getDefaultProperties(),
    );

    this.featureManager.createCornerHandles(polygonFeature);
    this.featureManager.updateLinesFromFeatures();

    this.polygonFeature = polygonFeature;
    this.feature = polygonFeature;
    this.persistTacticalAnnotation();
    this.markDirty();
    return true;
  }

  override async save(): Promise<void> {
    const polygonFeature = this.polygonFeature;
    if (!polygonFeature) {
      return;
    }

    this.persistTacticalAnnotation();
    this.feature = polygonFeature;
    await super.save(polygonFeature);

    this.resetAnnotation();
  }
}
